import React, { useContext } from 'react';
import { Breadcrumb, Space } from 'antd';
import { ThemeContext } from '../../contexts/ThemeContext';

export interface IPageHeaderBreadcrumbItem {
    title: React.ReactNode;
    href?: string;
    onClick?: () => void;
}

export interface IPageHeaderProps {
    icon?: React.ReactNode;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    badge?: React.ReactNode;
    tag?: React.ReactNode;
    extra?: React.ReactNode;
    actions?: React.ReactNode;
    breadcrumb?: React.ReactNode | IPageHeaderBreadcrumbItem[];
    iconBg?: string;
    iconColor?: string;
    children?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

export default function PageHeader({
    icon,
    title,
    subtitle,
    badge,
    tag,
    extra,
    actions,
    breadcrumb,
    iconBg,
    iconColor,
    children,
    className,
    style,
}: IPageHeaderProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const rightContent = extra || actions;
    const badgeContent = badge || tag;

    const defaultGradient = 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)';

    return (
        <div
            className={className}
            style={{
                background: isDark ? '#141414' : '#ffffff',
                borderRadius: 14,
                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                boxShadow: isDark
                    ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                    : '0 2px 12px rgba(0, 0, 0, 0.04)',
                padding: '20px 24px',
                marginBottom: 0,
                transition: 'all 0.2s ease',
                ...style,
            }}
        >
            {breadcrumb && (
                <div style={{ marginBottom: 12 }}>
                    {Array.isArray(breadcrumb) ? (
                        <Breadcrumb items={breadcrumb} />
                    ) : (
                        breadcrumb
                    )}
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 16,
                }}
            >
                {/* Left: Icon + Title + Subtitle */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        flex: '1 1 auto',
                        minWidth: 260,
                    }}
                >
                    {icon && (
                        <div
                            style={{
                                width: 50,
                                height: 50,
                                borderRadius: 12,
                                background: iconBg || defaultGradient,
                                color: iconColor || '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 24,
                                flexShrink: 0,
                                boxShadow: isDark
                                    ? '0 4px 12px rgba(22, 119, 255, 0.2)'
                                    : '0 4px 14px rgba(22, 119, 255, 0.25)',
                            }}
                        >
                            {icon}
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                            }}
                        >
                            <h1
                                style={{
                                    margin: 0,
                                    fontSize: 20,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    color: isDark ? '#f0f0f0' : '#1e293b',
                                    letterSpacing: '-0.3px',
                                }}
                            >
                                {title}
                            </h1>

                            {badgeContent && (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {badgeContent}
                                </span>
                            )}
                        </div>

                        {subtitle && (
                            <div
                                style={{
                                    fontSize: 13,
                                    color: isDark ? '#8c8c8c' : '#64748b',
                                    lineHeight: 1.45,
                                    maxWidth: 720,
                                }}
                            >
                                {subtitle}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Actions / Extra */}
                {rightContent && (
                    <Space
                        size={10}
                        wrap
                        style={{
                            alignItems: 'center',
                        }}
                    >
                        {rightContent}
                    </Space>
                )}
            </div>

            {/* Optional Children / Footer Slot */}
            {children && (
                <div
                    style={{
                        marginTop: 18,
                        paddingTop: 16,
                        borderTop: `1px solid ${isDark ? '#262626' : '#f0f2f5'}`,
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
}
